import { http } from "@/shared/api/axios";
import type {
  ShiftCalendarResponse,
  ShiftResponse,
  CreateShiftRequest,
  UpdateShiftRequest,
  ShiftCalendarParams,
} from "../model/types";

type DraftFields = "version" | "clientId" | "seriesId" | "isLocked" | "guards";

/** Calendar item as sent by a backend that may predate the drag-and-drop fields. */
type ShiftCalendarWire = Omit<ShiftCalendarResponse, DraftFields> &
  Partial<Pick<ShiftCalendarResponse, DraftFields>>;

const normalizeCalendarShift = (shift: ShiftCalendarWire): ShiftCalendarResponse => ({
  ...shift,
  version: shift.version ?? 0,
  clientId: shift.clientId ?? 0,
  seriesId: shift.seriesId ?? null,
  isLocked: shift.isLocked ?? false,
  guards: shift.guards ?? [],
});

export const fetchShiftsCalendar = async (
  params: ShiftCalendarParams
): Promise<ShiftCalendarResponse[]> => {
  const response = await http.get<ShiftCalendarWire[]>(
    "/v1/scheduler/shifts",
    { params, paramsSerializer: { indexes: null } }
  );
  return response.data.map(normalizeCalendarShift);
};

export const fetchShiftById = async (id: number): Promise<ShiftResponse> => {
  const response = await http.get<ShiftResponse>(
    `/v1/scheduler/shifts/${id}`
  );
  return response.data;
};

export const createShift = async (
  data: CreateShiftRequest
): Promise<ShiftResponse[]> => {
  const response = await http.post<ShiftResponse[]>(
    "/v1/scheduler/shifts",
    data,
    { _skipErrorToast: true } as never
  );
  return response.data;
};

export const updateShift = async (
  id: number,
  data: UpdateShiftRequest
): Promise<ShiftResponse> => {
  const response = await http.put<ShiftResponse>(
    `/v1/scheduler/shifts/${id}`,
    data,
    { _skipErrorToast: true } as never
  );
  return response.data;
};

export const deleteShift = async (
  id: number,
  deleteEntireSeries?: boolean
): Promise<void> => {
  await http.delete(`/v1/scheduler/shifts/${id}`, {
    params: deleteEntireSeries ? { deleteEntireSeries: true } : undefined,
    _skipErrorToast: true,
  } as never);
};

export const publishShift = async (
  id: number,
  publishEntireSeries?: boolean
): Promise<void> => {
  await http.post(`/v1/scheduler/shifts/${id}/publish`, null, {
    params: publishEntireSeries ? { publishEntireSeries: true } : undefined,
  });
};

export const respondToShift = async (
  id: number,
  response: "accept" | "reject"
): Promise<void> => {
  await http.post(`/v1/scheduler/shifts/${id}/respond`, { response });
};

export const openShiftForClaim = async (id: number): Promise<void> => {
  await http.post(`/v1/scheduler/shifts/${id}/open-claims`);
};

export const closeShiftForClaim = async (id: number): Promise<void> => {
  await http.post(`/v1/scheduler/shifts/${id}/close-claims`);
};
